import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { LocationStatus } from '../../interfaces/workSite.interface';

interface LocationStatusChipProps {
    status?: LocationStatus;
    distanceMeters?: number;
    siteName?: string;
    latitude?: number;
    longitude?: number;
    accuracyMeters?: number;
    isMock?: boolean;
}

// Wording here is the product, not decoration — this chip is read by managers about
// real employees, and by employees about themselves. See task-14 brief:
// - LOW_ACCURACY must never read as a fence violation: it says the *reading* was
//   unreliable, never that the person's behaviour was.
// - NO_LOCATION means GPS was off/unavailable — routine, not evasion.
// - NO_SITE is a fact about configuration, never about the person.
// - OUTSIDE is "flagged for review", never "violation"/"fraud"/"cheating".
// - isMock is the one genuine integrity signal — state it factually.
const buildLabel = (
    status: LocationStatus,
    distanceMeters?: number,
    siteName?: string,
): string => {
    switch (status) {
        case 'INSIDE':
            return siteName ? `${siteName} · inside` : 'Inside work site';
        case 'OUTSIDE': {
            const distancePart = distanceMeters !== undefined ? `${distanceMeters} m outside` : 'Outside';
            const base = siteName ? `${siteName} · ${distancePart}` : distancePart;
            return `${base} — flagged for review`;
        }
        case 'LOW_ACCURACY':
            return siteName ? `Low accuracy reading near ${siteName}` : 'Low accuracy reading';
        case 'NO_LOCATION':
            return 'No location reported';
        case 'NO_SITE':
            return 'No work site configured';
        default:
            return '';
    }
};

type ChipColor = 'default' | 'warning' | 'error';

// isMock is reserved the strongest colour — it is the one genuine integrity signal.
// OUTSIDE gets warning (a review flag, not an alarm). Everything else (including
// LOW_ACCURACY / NO_LOCATION / NO_SITE, which are about the reading or the setup,
// never the person) stays neutral/default.
const chipColorFor = (status: LocationStatus, isMock?: boolean): ChipColor => {
    if (isMock) return 'error';
    if (status === 'OUTSIDE') return 'warning';
    return 'default';
};

// INSIDE and OUTSIDE are filled so a glance tells "resolved" from "flagged".
// LOW_ACCURACY / NO_LOCATION / NO_SITE render outlined — muted, and never
// visually confusable with the filled warning OUTSIDE chip.
const chipVariantFor = (status: LocationStatus, isMock?: boolean): 'filled' | 'outlined' => {
    if (isMock) return 'filled';
    if (status === 'INSIDE' || status === 'OUTSIDE') return 'filled';
    return 'outlined';
};

const LocationStatusChip: React.FC<LocationStatusChipProps> = ({
    status,
    distanceMeters,
    siteName,
    latitude,
    longitude,
    accuracyMeters,
    isMock,
}) => {
    if (!status || status === 'NOT_EVALUATED') {
        // NOT_EVALUATED: feature is off for this tenant. NO_STATUS: a historical row
        // that predates the feature. Neither is a fact worth showing — render nothing.
        return null;
    }

    // Colour alone is never an accessible carrier for the single most consequential
    // fact on the screen (invisible on a touch device with no hover, invisible to a
    // red/green colour-blind reader) — so a mock location always gets a visible,
    // factual suffix in the label itself, not just the colour and the tooltip.
    const baseLabel = buildLabel(status, distanceMeters, siteName);
    const label = isMock ? `${baseLabel} · simulated location` : baseLabel;
    const color = chipColorFor(status, isMock);
    const variant = chipVariantFor(status, isMock);

    const tooltipParts: string[] = [];
    if (accuracyMeters !== undefined) {
        tooltipParts.push(`Reported accuracy: ±${accuracyMeters} m`);
    }
    if (isMock) {
        tooltipParts.push('Device reported a simulated location');
    }
    const tooltipTitle = tooltipParts.join(' · ');

    const hasCoordinates = latitude !== undefined && longitude !== undefined;

    const chip = (
        <Chip
            size="small"
            label={label}
            color={color}
            variant={variant}
            component={hasCoordinates ? 'a' : 'div'}
            href={hasCoordinates ? `https://www.google.com/maps?q=${latitude},${longitude}` : undefined}
            target={hasCoordinates ? '_blank' : undefined}
            rel={hasCoordinates ? 'noopener noreferrer' : undefined}
            clickable={hasCoordinates}
        />
    );

    if (!tooltipTitle) {
        return chip;
    }

    return (
        <Tooltip title={tooltipTitle} arrow>
            {chip}
        </Tooltip>
    );
};

export default LocationStatusChip;
